import { createSignal, Show, Signal, createEffect } from "solid-js";
import STInput from "../st-input/st-input";
import { toast } from 'common'
import './inline-edit.css'

export class InlineEditProps {
    disabled?: boolean
    model: Signal<string> = createSignal('')
    onSave?: (v: string) => Promise<void> | void
}

interface InlineEditInfo {
    el: HTMLElement
    save: () => Promise<void>
}

let activeEdit: InlineEditInfo | null = null

const onMouseupInlineEdit = (ev: MouseEvent) => {
    if (activeEdit && !activeEdit.el.contains(ev.target as HTMLElement)) {
        // Capture reference before save() � Solid's synchronous reactivity will
        // null activeEdit via the isEditing effect before this function returns.
        const edit = activeEdit
        edit.save()
    }
}

export default function InlineEdit(props: InlineEditProps) {
    let container: HTMLDivElement | undefined;
    let input: HTMLInputElement | undefined;

    const [isEditing, setIsEditing] = createSignal(false)
    const [value, setValue] = props.model
    const [isSaving, setIsSaving] = createSignal(false)

    let preEditValue = ''

    createEffect(() => {
        if (isEditing()) {
            document.addEventListener('mouseup', onMouseupInlineEdit)
            activeEdit = { el: container!, save }
            // Defer focus so <Show> has committed its DOM before we try to focus
            setTimeout(() => {
                input?.focus()
                input?.select()
            }, 0)
        } else {
            document.removeEventListener('mouseup', onMouseupInlineEdit)
            if (activeEdit?.el === container) {
                activeEdit = null
            }
        }
    })

    const enterEdit = (e: MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
        e.stopImmediatePropagation()

        if (!props.disabled && !isSaving()) {
            preEditValue = value()
            setIsEditing(true)
        }
    }

    const save = async () => {
        setIsEditing(false)
        if (value() === preEditValue) return

        setIsSaving(true)
        try {
            if (props.onSave && typeof props.onSave === 'function') {
                await props.onSave(value())
            }
        } catch (e) {
            setValue(preEditValue)
            const msg = typeof e === 'string' ? e : 'Failed to save. Your change has been reverted.'
            toast.notify('error', msg)
        } finally {
            setIsSaving(false)
        }
    }

    const cancel = () => {
        setValue(preEditValue)
        setIsEditing(false)
    }

    const onKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            save()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            cancel()
        }
    }

    return (
        <div class="inline-edit" ref={container}>
            <Show when={!isEditing()}>
                <div
                    class="inline-edit-value"
                    classList={{ 'inline-edit-saving': isSaving(), 'inline-edit-disabled': !!props.disabled }}
                    onClick={!props.disabled ? enterEdit : undefined}
                >
                    <div class="inline-edit-text">{value()}</div>
                    <Show when={!props.disabled}>
                        <button class="inline-edit-button" onClick={enterEdit} tabindex={-1} aria-label="Edit">
                            <span class="fa fa-edit"></span>
                        </button>
                    </Show>
                </div>
            </Show>
            <Show when={isEditing()}>
                <div class="inline-edit-editor">
                    <STInput ref={input} value={props.model} onKeyDown={onKeydown}></STInput>
                </div>
            </Show>
        </div>
    )
}
InlineEdit.defaultProps = new InlineEditProps()
